<template>
    <div class="ix-study-stats mt-3" v-if="draft.selected.length">
        <div class="ix-study-stat">
            <span class="ix-study-stat-title">Prix tarif du kit</span>
            <span class="ix-study-stat-value">{{ kitPrice !== null ? draft.currency(kitPrice) : '-' }}</span>
            <span class="ix-text-small ix-text-secondary d-block" v-if="!manual">Total des composants</span>
            <button type="button" class="ix-btn link p-0 ix-text-small d-block mx-auto" v-else-if="draft.sellingTotal > 0" @click="useTotal">
                Utiliser le total des composants ({{ draft.currency(draft.sellingTotal) }})
            </button>
        </div>
        <div class="ix-study-stat">
            <span class="ix-study-stat-title">
                Marge sur le kit
                <span v-b-tooltip title="Prix tarif du kit - prix de revient"><i class="fas fa-question-circle"></i></span>
            </span>
            <span class="ix-study-stat-value">
                <template v-if="margin !== null">
                    <span :class="{ 'ix-text-red': margin < 0 }">{{ draft.currency(margin) }}</span>
                    <template v-if="marginRate !== null">{{ marginSentence }} {{ percent(marginRate) }}</template>
                </template>
                <template v-else>-</template>
            </span>
        </div>
        <div class="ix-study-stat">
            <span class="ix-study-stat-title">
                Écart avec le total des composants
                <span v-b-tooltip title="Prix tarif du kit - prix tarif des composants"><i class="fas fa-question-circle"></i></span>
            </span>
            <span class="ix-study-stat-value">
                <template v-if="gap !== null">
                    {{ gap > 0 ? '+ ' : '' }}{{ draft.currency(gap) }}
                    <span class="ix-text-secondary" v-if="gapRate !== null">({{ gap > 0 ? '+ ' : '' }}{{ percent(gapRate) }})</span>
                </template>
                <template v-else>-</template>
            </span>
        </div>
    </div>
</template>

<script>
    import draft from './kitDraft';

    /**
     * La synthèse de prix d'un kit, sous sa tarification. Le prix tarif suit le total des
     * composants jusqu'à ce qu'on le saisisse ; le lien « Utiliser le total » le lui rend.
     *
     * @see docs/DRI/Phase2/articles-composes.md § 8.2
     */
    export default {
        name: 'article-kit-pricing-summary',
        props: {
            sellingPriceInput: { type: String, default: 'selling_price' },
        },
        data() {
            return {
                draft,
                kitPrice: null,
                manual: false,
                // Rien n'est écrit dans le champ avant d'avoir comparé le prix enregistré au total.
                ready: false,
            };
        },
        computed: {
            margin() {
                if (this.kitPrice === null || draft.costTotal === null || !draft.selected.length) return null;

                return this.kitPrice - draft.costTotal;
            },
            /** Doit rester le miroir de KitComposition::marginRate(). */
            marginRate() {
                if (this.margin === null) return null;

                const base = draft.marginType === 2 ? this.kitPrice : draft.costTotal;

                return base > 0 ? this.margin / base * 100 : null;
            },
            marginSentence() {
                return draft.marginType === 2 ? 'soit un taux de marque de' : 'soit une marge de';
            },
            gap() {
                return this.kitPrice === null ? null : this.kitPrice - draft.sellingTotal;
            },
            gapRate() {
                return this.gap === null || draft.sellingTotal <= 0 ? null : this.gap / draft.sellingTotal * 100;
            },
        },
        methods: {
            percent(value) {
                return value.toFixed(2).replace('.', ',') + ' %';
            },
            input() {
                return document.getElementById(this.sellingPriceInput);
            },
            /** Une valeur posée par le code : elle ne compte pas comme une saisie. */
            write(value) {
                const input = this.input();
                if (input) {
                    input.value = value.toFixed(2);
                }
                this.kitPrice = value;
            },
            useTotal() {
                this.manual = false;
                this.write(draft.sellingTotal);
            },
            follow() {
                if (this.ready && !this.manual && draft.selected.length && draft.sellingTotal > 0) {
                    this.write(draft.sellingTotal);
                }
            },
            /** Toute frappe fige le prix, même un champ vidé pour le ressaisir : le lien rend le total. */
            onInput(event) {
                const value = parseFloat(event.target.value);

                this.kitPrice = isNaN(value) ? null : value;
                this.manual = true;
            },
        },
        watch: {
            'draft.sellingTotal'() {
                this.follow();
            },
        },
        mounted() {
            const input = this.input();
            if (!input) return;

            const value = parseFloat(input.value);
            const stored = isNaN(value) ? null : value;
            this.kitPrice = stored;
            input.addEventListener('input', this.onInput);

            // La composition se remplit au montage de la table, juste avant : son total est lu ensuite.
            this.$nextTick(() => {
                // Un prix enregistré égal au total le suit encore ; un autre reste tel quel.
                this.manual = stored !== null
                    && Math.round(stored * 100) !== Math.round(draft.sellingTotal * 100);
                this.ready = true;
                this.follow();
            });
        },
        beforeDestroy() {
            const input = this.input();
            if (input) {
                input.removeEventListener('input', this.onInput);
            }
        },
    }
</script>
